import { toast } from "@/components/common/Toast/Toast"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { getApiErrorCode } from "../../../../../api/apiError"
import { useReleaseDeclarationHold } from "../../../../../api/declarationsApi"
import type { HoldReason } from "../../../../shared/holdReason"

interface ExtractionHoldPanelProps {
  declarationId: string
  reason: HoldReason
  /** The footer's own Anfordern: the same inquiry composer, same context. */
  onRequest: () => void
}

const TITLE_KEY: Record<HoldReason, TranslationKeys> = {
  blank_template: "MC_DECL_V2_HOLD_BLANK_TITLE",
  unfilled_form: "MC_DECL_V2_HOLD_UNFILLED_TITLE",
  other: "MC_DECL_V2_HOLD_OTHER_TITLE",
}
const BODY_KEY: Record<HoldReason, TranslationKeys> = {
  blank_template: "MC_DECL_V2_HOLD_BLANK_BODY",
  unfilled_form: "MC_DECL_V2_HOLD_UNFILLED_BODY",
  other: "MC_DECL_V2_HOLD_OTHER_BODY",
}

/** `release-hold`'s own 409s, read the same way `reextract`'s are read
 *  elsewhere (see `useSupplierGateAssign`'s CALM_REEXTRACT_CODES): a code
 *  with real copy here, anything else (an unrecognised 4xx, a network
 *  failure) falls back to the generic error key. */
const RELEASE_ERROR_KEYS: Partial<Record<string, TranslationKeys>> = {
  NOT_HELD: "MC_DECL_V2_HOLD_RELEASE_NOT_HELD",
  ALREADY_PROCESSING: "MC_DECL_V2_HOLD_RELEASE_ALREADY_PROCESSING",
  ALREADY_APPROVED: "MC_DECL_V2_HOLD_RELEASE_ALREADY_APPROVED",
  HAS_REVIEW_DECISIONS: "MC_DECL_V2_HOLD_RELEASE_HAS_DECISIONS",
}

/** The mask body of a HELD declaration (TUR-1211 wave 3). The pipeline staged
 *  nothing, so there is no unit card to review; instead of an empty mask the
 *  reviewer reads why and gets the one action that moves it: request the
 *  completed declaration from the supplier. The button drives the footer's own
 *  Anfordern so the composer opens with the same supplier and context.
 *
 *  Both known holds also get a second, reviewer-only override (TUR-1211
 *  wave 3, product decision): the detector staged nothing because it read the
 *  document as blank, but it can be wrong (a filled return whose entries the
 *  supplier's tool flattened reads as our untouched template), so "Trotzdem
 *  auslesen" forces the pipeline to read the document anyway rather than only
 *  being able to request a new declaration. A false hold must cost at most one
 *  click. */
export function ExtractionHoldPanel({
  declarationId,
  reason,
  onRequest,
}: ExtractionHoldPanelProps) {
  const { translate } = useTranslation()
  const releaseHold = useReleaseDeclarationHold()
  const showReleaseAction = reason !== "other" && releaseHold.canRun

  const handleReleaseHold = () => {
    releaseHold.mutate(declarationId, {
      onError: (error: unknown) => {
        const code = getApiErrorCode(error)
        const key = code ? RELEASE_ERROR_KEYS[code] : undefined
        toast.error(translate(key ?? "MC_API_ERROR_GENERIC"))
      },
    })
  }

  return (
    <div className="sv2-hold" data-testid="extraction-hold-panel">
      <h2 className="sv2-hold-title">{translate(TITLE_KEY[reason])}</h2>
      <p className="sv2-hold-body">{translate(BODY_KEY[reason])}</p>
      {showReleaseAction && (
        <p className="sv2-hold-hint">
          {translate("MC_DECL_V2_HOLD_RELEASE_HINT")}
        </p>
      )}
      <div className="sv2-hold-actions">
        <button type="button" className="sv2-hold-request" onClick={onRequest}>
          {translate("MC_DECL_V2_FOOTER_REQUEST")}
        </button>
        {showReleaseAction && (
          <button
            type="button"
            className="sv2-hold-release"
            onClick={handleReleaseHold}
            disabled={releaseHold.isPending}
          >
            {releaseHold.isPending && (
              <Loader2 size={14} className="animate-spin" />
            )}
            {translate("MC_DECL_V2_HOLD_RELEASE_BUTTON")}
          </button>
        )}
      </div>
    </div>
  )
}
